import { useReducer, useMemo, useCallback } from "react";
import { TiCardActor, TiCardQueryResponse } from "../../../@types";
import {
  LoadingIndicator,
  NoData,
  RenderWhen
} from "../../../../../components";
import TiCardGeneralInfo from "./general/TiCardGeneralInfo";
import TiCardActors from "./general/TiCardActors";
import TiCardAnalyticsInfo from "./analytics/TiCardAnalyticsInfo";
import TiCardAdditionalInformation from "./additionalinformation/TiCardAdditionalInformation";
import TiCardPrevalenceGraph from "./analytics/TiCardPrevalenceGraph";
import TiCardAdditionalInformationReducer, {
  TiCardAdditionalInformationStateClosed
} from "./additionalinformation/TiCardAdditionalInformationReducer";
import { AdditionalInformationType } from "./additionalinformation/@ŧypes/TiCardAdditionalInformationReducer";
import SampleAttributionList from "../../../../samples/components/ioc/SampleAttributionList";
import Typography from "../../../../../components/typography/Typography";
import AIGeneratedIcon from "../../../../../components/icons/AIGeneratedIcon";
import { getCountryFromCountryCode } from "../../../../samples/components/ioc/utils";
import { IOCSummary } from "../../../../samples/@types/ioc";

interface Props {
  tiCardData?: TiCardQueryResponse;
  isTiCardDataLoading: boolean;
  sampleIOCData?: IOCSummary;
  iocDataLoading: boolean;
  noBorder?: boolean;
  prerenderGraphImage?: boolean;
}

const AttributionCardContent = ({
  tiCardData,
  isTiCardDataLoading,
  sampleIOCData,
  iocDataLoading,
  noBorder,
  prerenderGraphImage
}: Props) => {
  const combinedActors = useMemo(() => {
    const actorMap = new Map<string, TiCardActor>();
    const iocActors = sampleIOCData?.related_actors || [];
    const tiCardActors = tiCardData?.rl?.general?.actor || [];

    iocActors.forEach(actor =>
      actorMap.set(actor.name, {
        name: actor.name,
        country:
          getCountryFromCountryCode(actor.state_attribution?.country) ?? "",
        aliases: actor.aliases ?? []
      })
    );

    tiCardActors.forEach(actor => {
      if (actor.name) {
        actorMap.set(actor.name, actor);
      }
    });

    return Array.from(actorMap.values());
  }, [tiCardData, sampleIOCData]);

  const [state, dispatch] = useReducer(
    TiCardAdditionalInformationReducer,
    TiCardAdditionalInformationStateClosed
  );

  const handleActorClick = useCallback((actor: TiCardActor) => {
    dispatch({
      type: AdditionalInformationType.Actor,
      data: actor
    });
  }, []);

  const handleThreatNameDetailsClick = () => {
    if (!tiCardData?.rl.general) return;
    dispatch({
      type: AdditionalInformationType.Threat,
      data: tiCardData.rl.general
    });
  };

  const handleCloseDetails = () => {
    dispatch({
      type: AdditionalInformationType.Closed
    });
  };

  const containerClasses = [
    "ticard-content__container--attribution",
    noBorder ? "ticard-content__container--no-border" : ""
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={"ticard-content"}>
      <RenderWhen condition={isTiCardDataLoading && iocDataLoading}>
        <LoadingIndicator
          style={{ margin: "5rem 0" }}
          message={"Loading..."}
          data-testid="ticard_loading_indicator"
        />
      </RenderWhen>
      <RenderWhen
        condition={
          !isTiCardDataLoading &&
          !iocDataLoading &&
          !tiCardData &&
          !sampleIOCData
        }
      >
        <span className={"ticard-no-content"} data-testid="ticard_no_data">
          No available Threat Intelligence data
        </span>
      </RenderWhen>
      {tiCardData && (
        <div className={containerClasses}>
          <RenderWhen
            condition={!iocDataLoading}
            fallback={<LoadingIndicator />}
          >
            <div className="ticard-content__sample_attribution">
              <div className="flex flex-gap-2 h3">
                <Typography variant="h3">Sample Attribution</Typography>
                <AIGeneratedIcon />
              </div>
              <SampleAttributionList sampleIOCData={sampleIOCData} hideActors />
            </div>
          </RenderWhen>
          <div className={"ticard-content__information"}>
            <div className="h3">
              <Typography variant="h3">Malware Family Insights</Typography>
            </div>
            <TiCardGeneralInfo
              data={tiCardData}
              onActorClick={handleActorClick}
              onOpenThreatNameDetails={handleThreatNameDetailsClick}
              hideActors
            />
          </div>

          <RenderWhen
            condition={!iocDataLoading}
            fallback={<LoadingIndicator />}
          >
            <div className="ticard-content__related-actors">
              <div className="ticard-content__related-actors-list">
                <span className="ticard-content__related-actors-label">
                  Related Threat Actors:{" "}
                </span>
                {combinedActors.length > 0 ? (
                  <TiCardActors
                    actors={combinedActors}
                    onActorClick={handleActorClick}
                  />
                ) : (
                  <NoData />
                )}
              </div>
            </div>
          </RenderWhen>

          <div className={"ticard-content__analytics_and_prevalence"}>
            <TiCardAnalyticsInfo analyticsData={tiCardData.rl.analytics} />
            <TiCardPrevalenceGraph
              prevelanceData={tiCardData.rl.analytics.prevalence || []}
              prerenderGraphImage={prerenderGraphImage}
            />
          </div>
          <TiCardAdditionalInformation
            state={state}
            closeDetails={handleCloseDetails}
          />
        </div>
      )}
    </div>
  );
};

export default AttributionCardContent;
